import React from "react"

import { Trans, useTranslation } from "react-i18next"
import { useLocation, useNavigate } from "react-router-dom"

import { Box, Grid, Hidden, Skeleton } from "@mui/material"
import { Button, Typography } from "@synapse-analytics/synapse-ui"

import type { MetricsState } from "../../types/custom/projects"
import { customRound } from "../../utils/genericHelpers"
import { InfoContainer } from "../core/InfoContainer"
import { GraphHeader } from "../graphs/GraphHeader"

import styles from "./AccuracyCard.module.scss"

type Props = {
  accuracy: number | null
  cardHeight: number
  isLoading: boolean
  emptyState?: MetricsState
  range?: number
}

export function AccuracyCard(props: Readonly<Props>): React.ReactElement {
  const { accuracy, cardHeight, isLoading, emptyState, range } = props

  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { t } = useTranslation("status")

  return (
    <div className={"charts-paper"}>
      <GraphHeader
        title={t("classificationMetrics.accuracy.title")}
        tooltipText={
          <Trans
            ns="status"
            components={{ bold: <strong />, em: <em /> }}
            i18nKey="classificationMetrics.accuracy.tooltip"
          />
        }
        range={range}
        padding="1rem"
      />

      <Box style={{ height: cardHeight, padding: "16px 16px 0px 16px" }}>
        {isLoading ? (
          // response is still loading
          <Grid
            container
            direction="column"
            justifyContent="center"
            alignItems="center"
            className={styles.loadingContainer}
          >
            <Skeleton animation="wave" width="75%" height="20%" />
            <Skeleton animation="wave" width="50%" height="10%" />
          </Grid>
        ) : accuracy || accuracy === 0 ? (
          // Accuracy is NOT falsey OR is exactly 0
          <Grid container direction="column" justifyContent="center" alignItems="center" style={{ height: cardHeight }}>
            {/* Desktop label and value */}
            <Hidden mdDown>
              <Grid item marginBottom={3}>
                <Typography variant="h1-bold" className={styles.accuracyValue}>
                  {customRound(accuracy * 100, 2)}%
                </Typography>
              </Grid>
              <Grid item>
                <Typography variant="h1-regular" className={styles.accuracyLabel}>
                  {t("classificationMetrics.accuracy.label")}
                </Typography>
              </Grid>
            </Hidden>
            {/* Mobile label and value */}
            <Hidden mdUp>
              <Grid item>
                <Typography variant="h1-bold" className={styles.accuracyValueMobile}>
                  {customRound(accuracy * 100, 2)}%
                </Typography>
              </Grid>
              <Grid item>
                <Typography variant="h1-regular" className={styles.accuracyLabelMobile}>
                  {t("classificationMetrics.accuracy.label")}
                </Typography>
              </Grid>
            </Hidden>
          </Grid>
        ) : (
          // Accuracy is falsey AND NOT 0
          // i.e.: Accuracy is null ==> emptyState
          <Grid container direction="row" justifyContent="center" alignItems="center" className={"full-height"}>
            {emptyState?.title ? (
              <InfoContainer
                title={emptyState?.title}
                subtitle={emptyState?.description}
                action={
                  emptyState?.action ? (
                    <Button
                      variant="primary"
                      onClick={() => navigate(`${pathname.substring(0, pathname.indexOf("/models"))}/integration`)}
                      id="acc_card_empty_state_btn"
                    >
                      {emptyState?.action}
                    </Button>
                  ) : undefined
                }
              />
            ) : (
              <Typography variant="p">
                {t("classificationMetrics.accuracy.emptyState.noPredictionsOrFeedback")}
              </Typography>
            )}
          </Grid>
        )}
      </Box>
    </div>
  )
}
